import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, MapPin, Landmark, X, Navigation, Link as LinkIcon, ExternalLink, Star, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

// Hotel property-level details (v8 Round-3) — amenities, nearby landmarks,
// star class and a zero-setup location picker. Rendered only when
// `is_hotel` is true (see SchemaCardRenderer.jsx). These are plain
// listing-level `data` fields — no separate sub-resource, unlike Room
// Types. Location resolution reuses the existing authenticated
// `POST /api/vendor-geo/resolve` endpoint (browser GPS / manual lat-lng /
// pasted Google Maps link — no third-party map API key required).
const PROPERTY_AMENITY_OPTIONS = [
  'Swimming Pool', 'Lift / Elevator', 'Banquet Hall', 'Gym / Fitness Center', 'Spa',
  "Kids' Play Area", 'In-house Restaurant', 'Conference / Meeting Rooms',
  'Business Center / Printing', 'Car Parking', 'Wheelchair Accessible',
  'Airport Shuttle', 'Pet Friendly', 'Laundry Service', '24-Hour Front Desk',
  'Power Backup', 'Wi-Fi in Common Areas', 'CCTV Security', 'Doctor on Call',
  'Travel Desk', 'Currency Exchange', 'Bar / Lounge', 'Rooftop Terrace',
  'Bonfire Area', 'Bicycle Rental',
];
const PROPERTY_CLASS_OPTIONS = [
  { value: 'unrated', label: 'Unrated / Not Classified' },
  { value: '1_star', label: '1 Star' },
  { value: '2_star', label: '2 Star' },
  { value: '3_star', label: '3 Star' },
  { value: '4_star', label: '4 Star' },
  { value: '5_star', label: '5 Star' },
  { value: 'heritage', label: 'Heritage Property' },
  { value: 'luxury_boutique', label: 'Luxury Boutique' },
];

export const HotelPropertyDetailsCard = ({ formData, handleChange }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [landmarkName, setLandmarkName] = useState('');
  const [landmarkDistance, setLandmarkDistance] = useState('');
  const [mapsUrlInput, setMapsUrlInput] = useState('');
  const [manualLat, setManualLat] = useState(formData?.property_location?.lat ?? '');
  const [manualLng, setManualLng] = useState(formData?.property_location?.lng ?? '');
  const [locating, setLocating] = useState(false);
  const [resolvingLink, setResolvingLink] = useState(false);

  const amenities = formData?.property_amenities || [];
  const landmarks = formData?.nearby_landmarks || [];
  const location = formData?.property_location || null;

  const toggleAmenity = (amenity) => {
    handleChange('property_amenities', amenities.includes(amenity)
      ? amenities.filter((a) => a !== amenity)
      : [...amenities, amenity]);
  };

  const addLandmark = () => {
    if (!landmarkName.trim()) return;
    handleChange('nearby_landmarks', [...landmarks, { name: landmarkName.trim(), distance: landmarkDistance.trim() }]);
    setLandmarkName('');
    setLandmarkDistance('');
  };

  const removeLandmark = (idx) => {
    handleChange('nearby_landmarks', landmarks.filter((_, i) => i !== idx));
  };

  const applyResolved = (res) => {
    handleChange('property_location', {
      lat: res.latitude,
      lng: res.longitude,
      formatted_address: res.address
        ? [res.address.address_line1, res.address.city, res.address.state, res.address.postal_code].filter(Boolean).join(', ')
        : (location?.formatted_address || ''),
      geo_source: res.geo_source,
    });
    setManualLat(res.latitude);
    setManualLng(res.longitude);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast({ title: 'Not supported', description: 'Your browser does not support geolocation', variant: 'destructive' });
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const res = await apiCall('/api/vendor-geo/resolve', {
            method: 'POST',
            body: JSON.stringify({ browser_lat: pos.coords.latitude, browser_lng: pos.coords.longitude }),
          });
          if (res?.geo_verified) {
            applyResolved(res);
            toast({ title: 'Location set from your current position' });
          }
        } catch (error) {
          toast({ title: 'Could not resolve location', description: error.message, variant: 'destructive' });
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast({ title: 'Location access denied', description: err.message, variant: 'destructive' });
      },
    );
  };

  const saveManualCoords = async () => {
    const lat = parseFloat(manualLat);
    const lng = parseFloat(manualLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      toast({ title: 'Invalid coordinates', description: 'Enter numeric latitude and longitude', variant: 'destructive' });
      return;
    }
    try {
      const res = await apiCall('/api/vendor-geo/resolve', {
        method: 'POST',
        body: JSON.stringify({ browser_lat: lat, browser_lng: lng }),
      });
      if (res?.geo_verified) applyResolved(res);
    } catch (error) {
      toast({ title: 'Could not save coordinates', description: error.message, variant: 'destructive' });
    }
  };

  const resolveFromMapsLink = async () => {
    if (!mapsUrlInput.trim()) return;
    setResolvingLink(true);
    try {
      const res = await apiCall('/api/vendor-geo/resolve', {
        method: 'POST',
        body: JSON.stringify({ google_maps_url: mapsUrlInput.trim() }),
      });
      if (res?.geo_verified) {
        applyResolved(res);
        toast({ title: 'Location resolved from Google Maps link' });
      } else {
        toast({ title: 'Could not resolve link', description: 'That link did not contain a recognisable location', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Could not resolve link', description: error.message, variant: 'destructive' });
    } finally {
      setResolvingLink(false);
    }
  };

  const clearLocation = () => {
    handleChange('property_location', null);
    setManualLat('');
    setManualLng('');
    setMapsUrlInput('');
  };

  return (
    <Card data-testid="hotel-property-details-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4" /> Property Details</CardTitle>
        <CardDescription>Amenities, nearby landmarks, star class and location for the whole property (separate from per-room-type details above).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Property Amenities */}
        <div>
          <Label>Property Amenities</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PROPERTY_AMENITY_OPTIONS.map((a) => (
              <Badge
                key={a}
                variant={amenities.includes(a) ? 'default' : 'outline'}
                className="cursor-pointer select-none"
                onClick={() => toggleAmenity(a)}
                data-testid={`property-amenity-${a.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              >
                {a}
              </Badge>
            ))}
          </div>
        </div>

        {/* Nearby Landmarks */}
        <div className="pt-4 border-t">
          <Label className="flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5" /> Nearby Landmarks / Tourist Spots</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {landmarks.map((lm, idx) => (
              <Badge key={`${lm.name}-${idx}`} variant="secondary" className="font-normal pr-1" data-testid={`landmark-chip-${idx}`}>
                {lm.name}{lm.distance ? ` — ${lm.distance}` : ''}
                <button type="button" onClick={() => removeLandmark(idx)} className="ml-1.5 align-middle" data-testid={`remove-landmark-${idx}`}>
                  <X className="h-3 w-3 inline" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Input
              value={landmarkName} onChange={(e) => setLandmarkName(e.target.value)}
              placeholder="e.g. Indira Gandhi International Airport" className="flex-1"
              data-testid="new-landmark-name"
            />
            <Input
              value={landmarkDistance} onChange={(e) => setLandmarkDistance(e.target.value)}
              placeholder="e.g. 8 km" className="w-32"
              data-testid="new-landmark-distance"
            />
            <Button type="button" variant="outline" onClick={addLandmark} data-testid="add-landmark-btn">Add</Button>
          </div>
        </div>

        {/* Property Class */}
        <div className="pt-4 border-t">
          <Label className="flex items-center gap-1.5"><Star className="h-3.5 w-3.5" /> Property Class</Label>
          <p className="text-xs text-gray-500 mb-1.5">Official star rating / classification — separate from customer review ratings.</p>
          <Select value={formData?.property_class || ''} onValueChange={(v) => handleChange('property_class', v)}>
            <SelectTrigger data-testid="property-class-select" className="max-w-xs"><SelectValue placeholder="Select property class" /></SelectTrigger>
            <SelectContent>
              {PROPERTY_CLASS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Location */}
        <div className="pt-4 border-t">
          <Label className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Property Location</Label>
          <p className="text-xs text-gray-500 mb-2">No map account needed — use your current location, type coordinates, or paste a Google Maps link.</p>

          {location?.lat != null && (
            <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 mb-3" data-testid="property-location-summary">
              <div className="text-sm">
                <p className="font-medium">{location.formatted_address || `${location.lat}, ${location.lng}`}</p>
                <p className="text-xs text-gray-500">{location.lat.toFixed?.(6) ?? location.lat}, {location.lng.toFixed?.(6) ?? location.lng}</p>
              </div>
              <div className="flex gap-2">
                <a
                  href={`https://www.google.com/maps?q=${location.lat},${location.lng}`}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="view-on-google-maps-link"
                >
                  <Button type="button" size="sm" variant="outline"><ExternalLink className="h-3.5 w-3.5 mr-1" /> View on Google Maps</Button>
                </a>
                <Button type="button" size="sm" variant="ghost" onClick={clearLocation} data-testid="clear-property-location-btn">Clear</Button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-end">
            <Button type="button" size="sm" onClick={useCurrentLocation} disabled={locating} data-testid="use-current-location-btn">
              {locating ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Navigation className="h-3.5 w-3.5 mr-1.5" />} Use My Current Location
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-3 max-w-md">
            <div>
              <Label className="text-xs">Latitude</Label>
              <Input value={manualLat} onChange={(e) => setManualLat(e.target.value)} placeholder="e.g. 19.0760" data-testid="manual-lat-input" />
            </div>
            <div>
              <Label className="text-xs">Longitude</Label>
              <Input value={manualLng} onChange={(e) => setManualLng(e.target.value)} placeholder="e.g. 72.8777" data-testid="manual-lng-input" />
            </div>
          </div>
          <Button type="button" size="sm" variant="outline" className="mt-2" onClick={saveManualCoords} data-testid="save-manual-coords-btn">
            Save Coordinates
          </Button>

          <div className="mt-3 max-w-md">
            <Label className="text-xs flex items-center gap-1"><LinkIcon className="h-3 w-3" /> Or paste a Google Maps link</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={mapsUrlInput} onChange={(e) => setMapsUrlInput(e.target.value)}
                placeholder="https://maps.google.com/?q=..." data-testid="maps-url-input"
              />
              <Button type="button" variant="outline" onClick={resolveFromMapsLink} disabled={resolvingLink} data-testid="resolve-maps-link-btn">
                {resolvingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Resolve'}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default HotelPropertyDetailsCard;
